import {styled} from '@mui/material/styles';
import {
  DataGridPro,
  type DataGridProProps,
  gridClasses,
} from '@mui/x-data-grid-pro';
import React, {useMemo} from 'react';

import {
  Color,
  hexToRGB,
  OBLIVION,
  RED_300,
  TEAL_300,
  WHITE,
} from '../../../../common/css/globals.styles';
import {Loading} from '../../../Loading';
import {protectColumnsFromErrors} from './pages/CallsPage/CellErrorBoundary';

// Class name constants
export const SELECTED_FOR_DELETION = 'selected-for-deletion';

const backgroundColorHovered = 'var(--wb-bg-hover)';
const backgroundColorSelected = `light-dark(${hexToRGB(
  TEAL_300,
  0.32
)}, color-mix(in srgb, var(--teal-500) 32%, transparent))`;
const backgroundColorHoveredSelectedLight = Color.fromHex(WHITE)
  .blend(Color.fromHex(TEAL_300, 0.32))
  .blend(Color.fromHex(OBLIVION, 0.04))
  .toString();
const backgroundColorHoveredSelected = `light-dark(${backgroundColorHoveredSelectedLight}, color-mix(in srgb, var(--teal-500) 32%, transparent))`;
const backgroundColorSelectedForDeletion = `light-dark(${hexToRGB(
  RED_300,
  0.32
)}, color-mix(in srgb, var(--red-500) 32%, transparent))`;

// Use our custom loading component that matches our palette.
const LoadingOverlay = () => <Loading centered />;

// Pinning `scrollbarSize` lets MUI skip a per-commit forced-reflow measurement.
// Trade-off: cached for the page's lifetime, so a runtime change (macOS mouse
// plug/unplug toggling overlay vs classic scrollbars, browser zoom) won't be
// reflected until reload. Acceptable: scrollbar mode rarely changes mid-session.
const SCROLLBAR_SIZE = measureBrowserScrollbarOnce();
function measureBrowserScrollbarOnce(): number {
  if (typeof document === 'undefined') {
    return 0;
  }
  const probe = document.createElement('div');
  probe.style.cssText =
    'width:99px;height:99px;position:absolute;overflow:scroll;visibility:hidden;top:-9999px;';
  document.body.appendChild(probe);
  const size = probe.offsetWidth - probe.clientWidth;
  document.body.removeChild(probe);
  return size;
}

export const StyledDataGrid = styled(
  ({
    keepBorders,
    ...otherProps
  }: DataGridProProps & {keepBorders?: boolean}) => {
    const slots = otherProps.slots ?? {};
    if (!slots.loadingOverlay) {
      slots.loadingOverlay = LoadingOverlay;
    }
    const protectedColumns = useMemo(() => {
      return protectColumnsFromErrors(otherProps.columns);
    }, [otherProps.columns]);
    return (
      <DataGridPro
        scrollbarSize={SCROLLBAR_SIZE}
        slots={slots}
        resizeThrottleMs={200}
        {...otherProps}
        columns={protectedColumns}
      />
    );
  }
)(({keepBorders}) => ({
  ...(!keepBorders ? {borderRight: 0, borderLeft: 0, borderBottom: 0} : {}),

  fontFamily: 'Source Sans Pro',

  '& .MuiDataGrid-columnHeader': {
    backgroundColor: 'var(--wb-bg-surface-sunken)',
    color: 'var(--wb-text-tertiary)',
  },

  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 400,
  },

  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
    {
      outline: 'none',
    },

  '& .MuiTablePagination-displayedRows': {
    margin: 0,
  },

  '& .MuiTablePagination-actions': {
    marginRight: '16px',
  },

  [`& .${gridClasses.row}`]: {
    '&.Mui-hovered': {
      backgroundColor: backgroundColorHovered,
    },
    '&.Mui-selected': {
      backgroundColor: backgroundColorSelected,
      '&.Mui-hovered': {
        backgroundColor: backgroundColorHoveredSelected,
      },
    },
    [`&.${SELECTED_FOR_DELETION}`]: {
      backgroundColor: backgroundColorSelectedForDeletion,
      '&.Mui-hovered': {
        backgroundColor: backgroundColorSelectedForDeletion,
      },
    },
  },
}));
StyledDataGrid.displayName = 'StyledDataGrid';
